'use client'
import PropTypes from 'prop-types'
import NextLink from 'next/link'
import React, { memo, useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'

export const Loader = () => {
  return (
    <React.Fragment>
      <div className="flex justify-center items-center">
        <div
          className="spinner-border border-r-transparent animate-spin inline-block w-5 h-5 border-4 mr-2 rounded-full"
          role="status"
        />
      </div>
    </React.Fragment>
  )
}

const CustomButton = ({
  children,
  href,
  type = 'button',
  variant = 'primary',
  className,
  style,
  isLoading,
  ...props
}) => {
  const [mounted, setMounted] = useState(false)

  const classess = {
    base: 'inline-flex items-center justify-center border rounded-lg hover:duration-300 hover:transition-all',
    primary:
      'py-3 px-4 inline-flex disabled:opacity-60 disabled:hover:bg-v8-blue h-fit justify-center w-full transition duration-300 whitespace-nowrap text-sm sm:text-base font-semibold border border-v8-blue rounded-lg text-white bg-v8-blue shadow-sm hover:bg-v8-blue/60 focus:outline-none',
    outlined:
      'py-3 px-4 inline-flex  h-fit justify-center w-full whitespace-nowrap text-sm sm:text-base font-semibold border border-v8-line rounded-lg text-v8-ink bg-white shadow-sm hover:bg-v8-blue/5 focus:outline-none',
    destructive:
      'py-3 px-4 inline-flex h-fit justify-center w-full transition duration-300 whitespace-nowrap text-sm sm:text-base font-semibold bg-red-500 disabled:bg-red-400 border border-red-500 rounded-lg text-white shadow-sm hover:bg-red-400 focus:outline-none',
    positive:
      'py-3 px-4 inline-flex h-fit justify-center w-full transition duration-300 whitespace-nowrap text-sm sm:text-base font-semibold bg-cgreen-550 disabled:bg-cgreen-700 border border-cgreen-550 rounded-lg text-white shadow-sm hover:bg-cgreen-500 focus:outline-none',
    disabled:
      'py-2 px-6 border-primary bg-primary text-tertiary-color font-bold text-base cursor-not-allowed opacity-50',
    'outlined-green':
      'py-2 px-9 border-green-font-color font-semibold text-green-font-color uppercase text-sm hover:bg-primary hover:text-white',
    'outlined-green-lowercase':
      'py-2 px-9 border-green-font-color font-semibold text-green-font-color text-sm hover:bg-primary hover:text-white',
    'no-outlined': 'py-2 px-9 border-0 text-green-font-color text-sm',
  }

  useEffect(() => {
    setMounted(true)
  }, [])

  const BaseButton = ({ ...p }) => {
    return (
      <button type={type} {...p}>
        {isLoading ? <Loader /> : <></>}
        {children}
      </button>
    )
  }

  const LinkButton = ({ ...p }) => {
    return (
      mounted && (
        <NextLink href={href}>
          <a {...p}>{children}</a>
        </NextLink>
      )
    )
  }

  const Comp = href ? LinkButton : BaseButton

  return (
    <Comp
      className={twMerge(classess['base'], classess[variant], className)}
      style={style}
      disabled={isLoading || variant === 'disabled'}
      {...props}
    >
      {children}
    </Comp>
  )
}

CustomButton.propTypes = {
  children: PropTypes.node.isRequired,
  href: PropTypes.string,
  type: PropTypes.string,
  variant: PropTypes.oneOf([
    'primary',
    'outlined',
    'destructive',
    'positive',
    'outlined-green',
    'no-outlined',
  ]),
  className: PropTypes.string,
  style: PropTypes.object,
  isLoading: PropTypes.bool,
}

export default memo(CustomButton)
